import { z } from 'zod';

/**
 * How the voice agent says a person's name aloud (ADR-321, ADR-342).
 *
 * The practice writes ONE line, the spoken name — "Doctor Sahai" — and the
 * agent says exactly that. Blank means the suggestion: the line this file
 * builds from the chart name and the role. The same builder turns a referring
 * doctor's name, as a fax or email spelled it, into the line the agent says
 * about them.
 *
 * The text goes straight to text-to-speech, which reads what it is given: "Dr."
 * comes out as "Drive" or "D, R", "MD" is spelled letter by letter, and a
 * period is a pause. So the line is written exactly as it is said — words in
 * full, nothing to expand. Everything here is a fixed lexical table over typed
 * or charted names, not intent detection.
 */

export const SPOKEN_NAME_MAX_LENGTH = 80;

// -----------------------------------------------------------------------------
// Tables
// -----------------------------------------------------------------------------

/**
 * A credential written after a name, and the title it puts in front of the
 * spoken name (null: none, the name is said alone). The credential itself is
 * never said. Doctoral credentials say "Doctor"; a nurse practitioner's say
 * "Nurse Practitioner"; everything else gives no title. To add one, add a row.
 */
const CREDENTIALS: ReadonlyArray<{ written: string; title: string | null }> = [
  { written: 'MD', title: 'Doctor' },
  { written: 'DO', title: 'Doctor' },
  { written: 'DDS', title: 'Doctor' },
  { written: 'DMD', title: 'Doctor' },
  { written: 'DPM', title: 'Doctor' },
  { written: 'PhD', title: 'Doctor' },
  { written: 'PsyD', title: 'Doctor' },
  { written: 'DNP', title: 'Doctor' },
  { written: 'OD', title: 'Doctor' },
  { written: 'DC', title: 'Doctor' },
  { written: 'NP', title: 'Nurse Practitioner' },
  { written: 'FNP', title: 'Nurse Practitioner' },
  { written: 'ANP', title: 'Nurse Practitioner' },
  { written: 'AGNP', title: 'Nurse Practitioner' },
  { written: 'PMHNP', title: 'Nurse Practitioner' },
  { written: 'ARNP', title: 'Nurse Practitioner' },
  { written: 'FNP-C', title: 'Nurse Practitioner' },
  { written: 'FNP-BC', title: 'Nurse Practitioner' },
  { written: 'AGNP-C', title: 'Nurse Practitioner' },
  { written: 'PA', title: null },
  { written: 'PA-C', title: null },
  { written: 'RN', title: null },
  { written: 'LPN', title: null },
  { written: 'LVN', title: null },
  { written: 'MA', title: null },
  { written: 'CRNA', title: null },
  { written: 'APRN', title: null },
  { written: 'CNM', title: null },
  { written: 'LCSW', title: null },
  { written: 'LMFT', title: null },
  { written: 'LPC', title: null },
  // Degrees and fellowships written after a clinical credential — never said.
  { written: 'MPH', title: null },
  { written: 'MS', title: null },
  { written: 'MSN', title: null },
  { written: 'BSN', title: null },
  { written: 'MBA', title: null },
  { written: 'MBBS', title: null },
  { written: 'FACC', title: null },
  { written: 'FACS', title: null },
  { written: 'FACP', title: null },
  { written: 'FAAP', title: null },
  { written: 'FAAFP', title: null },
  { written: 'FACOG', title: null },
];

/**
 * A credential the table does not list, as it is written after a comma: one
 * word of two to six capitals, optionally hyphenated ("MPH", "FNP-BC"). It is
 * dropped and gives no title — reading it as a name would put "Mph" in front of
 * a referrer's name.
 */
const UNLISTED_CREDENTIAL = /^[A-Z]{2,6}(-[A-Z]{1,3})?$/;

const CREDENTIAL_BY_KEY = new Map(CREDENTIALS.map((c) => [c.written.toLowerCase(), c]));

/** Titles written before a name, each with the word said. */
const TITLES = new Map<string, string>([
  ['dr', 'Doctor'],
  ['doctor', 'Doctor'],
  ['prof', 'Professor'],
  ['professor', 'Professor'],
  ['mr', 'Mister'],
  ['mister', 'Mister'],
]);

/**
 * Courtesy titles with no single spoken word. A suggestion leaves them out (a
 * practice names a clinician by their title or not at all); a typed line keeps
 * them so the check below asks the practice to write what it means.
 */
const COURTESY_TITLES = new Set(['mrs', 'ms', 'mx', 'miss']);

/** Generational suffixes written after a name, each with the words said. */
const SUFFIXES = new Map<string, string>([
  ['jr', 'Junior'],
  ['junior', 'Junior'],
  ['sr', 'Senior'],
  ['senior', 'Senior'],
  ['ii', 'the Second'],
  ['iii', 'the Third'],
  ['iv', 'the Fourth'],
]);

/**
 * Abbreviations a typed line still rejects after expansion, with the word to
 * write (null: no single word). Built from the tables above so a title or
 * suffix added there is reported here too; "Miss" is a word, and a numeral
 * suffix is caught as an initialism.
 */
const ABBREVIATIONS = new Map<string, string | null>([
  ...[...TITLES, ...SUFFIXES].filter(
    ([key, word]) => key !== word.toLowerCase() && !word.startsWith('the ')
  ),
  ...[...COURTESY_TITLES].filter((key) => key !== 'miss').map((key) => [key, null] as const),
  ['drs', 'Doctors'],
  ['st', 'Saint'],
  ['phd', null],
  ['psyd', null],
]);

// Letters (any script, with their accents), spaces, hyphens and apostrophes
// (straight or curly).
const ALLOWED = /^[\p{L}\p{M}' ’-]+$/u;
const NOT_ALLOWED = /[^\p{L}\p{M}'’-]/gu;
const APOSTROPHES = /['’]/g;

// -----------------------------------------------------------------------------
// The typed line
// -----------------------------------------------------------------------------

/** Trim and collapse internal whitespace — the stored form. */
export function normalizeSpokenName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/**
 * Whether two spoken lines say the same thing (ADR-342): case and spacing do
 * not count, because text-to-speech reads them alike. The ONE comparison for
 * "is this line the suggestion?" — the API uses it to store null, the editor
 * and the detail sheets use it to show the Suggested badge, so the three agree.
 * Works for a spoken name and a spoken address alike.
 */
export function sameSpokenLine(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return normalizeSpokenName(a ?? '').toLowerCase() === normalizeSpokenName(b ?? '').toLowerCase();
}

/**
 * Why `name` can't be spoken as written, as a message for the person typing it,
 * or null when it can. Blank is fine (it means "use the suggestion"). Pass the
 * normalized form; run `expandSpokenName` first so only what has no expansion
 * is reported.
 */
export function spokenNameIssue(name: string): string | null {
  if (name === '') return null;
  if (name.length > SPOKEN_NAME_MAX_LENGTH) {
    return `Keep it under ${SPOKEN_NAME_MAX_LENGTH} characters`;
  }
  if (name.includes('.')) {
    return 'Remove periods and write each word in full, e.g. "Doctor", not "Dr."';
  }
  if (!ALLOWED.test(name)) {
    return 'Use letters, spaces, hyphens and apostrophes only — the voice agent reads out anything else';
  }
  for (const word of name.split(/[ -]+/).filter(Boolean)) {
    const letters = word.replace(APOSTROPHES, '');
    const key = letters.toLowerCase();
    if (ABBREVIATIONS.has(key)) {
      const instead = ABBREVIATIONS.get(key);
      return instead
        ? `Write "${instead}" instead of "${word}"`
        : `"${word}" is an abbreviation — write the name the way the voice agent should say it`;
    }
    // Two or more capitals with no lowercase ("MD", "AJ") — TTS reads an
    // initialism letter by letter.
    if (letters.length >= 2 && letters === letters.toUpperCase() && letters !== key) {
      return `"${word}" would be spelled out letter by letter — write it as a word, or leave it out`;
    }
  }
  return null;
}

/**
 * A typed spoken line with its abbreviations written out: "Dr. Sahai" →
 * "Doctor Sahai", "Anurag Sahai, MD" → "Doctor Anurag Sahai", "John Smith Jr"
 * → "John Smith Junior". The words the practice chose, in the order it chose,
 * are kept; only what the tables know is expanded, so "Ms" and an unknown
 * initialism are left for `spokenNameIssue` to report. Idempotent.
 */
export function expandSpokenName(typed: string): string {
  const parsed = parseName(normalizeSpokenName(typed));
  const title = parsed.writtenTitle ?? titleForCredentials(parsed.credentials);
  return joinSpoken(title, parsed.courtesy, parsed.words, parsed.suffix);
}

/**
 * A spoken-line request field: normalized, expanded, validated, and blank
 * stored as null so an empty box goes back to the suggestion.
 */
export const spokenNameSchema: z.ZodType<string | null, string> = z
  .string()
  .transform((text) => expandSpokenName(text))
  .superRefine((text, ctx) => {
    const issue = spokenNameIssue(text);
    if (issue) ctx.addIssue({ code: 'custom', message: issue });
  })
  .transform((text) => (text === '' ? null : text));

// -----------------------------------------------------------------------------
// The suggestion
// -----------------------------------------------------------------------------

/**
 * The title a role maps to (ADR-330). Each row lists every spelling of a role
 * that means the title: the setup dropdown's word ("Physician") and the forms
 * EHR sync writes into `role`, which is the chart credential as the EHR spells
 * it ("MD", "DOCTOR OF OSTEOPATHY (DO)"). To map another role, add a row or a
 * spelling here.
 */
export const AUTOMATIC_ROLE_TITLES: ReadonlyArray<{ title: string; roles: readonly string[] }> = [
  {
    title: 'Doctor',
    roles: ['Physician', 'MD', 'DO', 'Doctor of Osteopathy', 'Doctor of Osteopathy (DO)'],
  },
  {
    title: 'Nurse Practitioner',
    roles: [
      'Nurse Practitioner',
      'NP',
      'Nurse Practitioner (NP)',
      'Nurse Practitioner, Supervising (NP,S)',
    ],
  },
];

/** Case, spacing and punctuation don't distinguish two spellings of a role. */
function canonicalRole(role: string): string {
  return role.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const TITLE_BY_ROLE = new Map(
  AUTOMATIC_ROLE_TITLES.flatMap(({ title, roles }) =>
    roles.map((role) => [canonicalRole(role), title] as const)
  )
);

/**
 * The title a role maps to, or null. The WHOLE role has to match a listed
 * spelling — never a part of it, so "Physician Assistant" is not "Physician".
 */
export function titleForRole(role: string | null | undefined): string | null {
  if (!role) return null;
  return TITLE_BY_ROLE.get(canonicalRole(role)) ?? null;
}

/** "Doctor" for any doctoral credential, else "Nurse Practitioner" for an NP's, else null. */
function titleForCredentials(credentials: readonly string[]): string | null {
  const titles = credentials.map((c) => CREDENTIAL_BY_KEY.get(c.toLowerCase())?.title ?? null);
  if (titles.includes('Doctor')) return 'Doctor';
  if (titles.includes('Nurse Practitioner')) return 'Nurse Practitioner';
  return null;
}

/**
 * What the voice agent says for one of the practice's providers when nobody has
 * written a spoken name: a title, then the cleaned chart name. "Anurag, MD
 * Sahai, MD" → "Doctor Anurag Sahai".
 *
 * The title is the one the role maps to (ADR-330), else one written in the
 * chart name ("Dr"), else the credential's (MD, DO, DDS, DMD, DPM, PhD, PsyD,
 * DNP, OD, DC → "Doctor"; NP → "Nurse Practitioner"), else none. The name is
 * the chart name with credentials and courtesy titles removed, repeated words
 * collapsed, suffixes written out, and an all-caps or all-lowercase word put in
 * name case. The result always passes `spokenNameIssue`, so it can sit in the
 * setup box as it is. '' when the chart has no name.
 */
export function suggestSpokenName(p: {
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
}): string {
  const parsed = parseName(normalizeSpokenName(`${p.firstName ?? ''} ${p.lastName ?? ''}`));
  return suggestSpokenNameFromParsed(parsed, p.role);
}

/** `suggestSpokenName` for a chart name already read with `parseName`, so a caller holding one parses once. */
export function suggestSpokenNameFromParsed(
  parsed: ParsedName,
  role: string | null | undefined
): string {
  return spokenFromChart(parsed, titleForRole(role));
}

/**
 * What the voice agent says for a referring doctor, from the name as the
 * referral spelled it — free text off a fax, so also "Last, First": "DEVINENI,
 * ADITYA MD" → "Doctor Aditya Devineni", "Dr. Wilson" → "Doctor Wilson". There
 * is no role and no box to correct it in, so the title comes from the name
 * alone. '' when nothing name-like is left.
 */
export function spokenReferrerName(raw: string | null | undefined): string {
  const parsed = parseName(normalizeSpokenName(raw ?? ''), { lastNameFirst: true });
  return spokenFromChart(parsed, null);
}

function spokenFromChart(parsed: ParsedName, roleTitle: string | null): string {
  const words = parsed.words.map(cleanChartWord).filter(Boolean);
  if (words.length === 0) return '';
  const title = roleTitle ?? parsed.writtenTitle ?? titleForCredentials(parsed.credentials);
  return joinSpoken(title, [], words, parsed.suffix);
}

/** Letters only, in name case when the chart shouted or whispered it ("SAHAI", "sahai" → "Sahai"). */
function cleanChartWord(word: string): string {
  const letters = word.replace(NOT_ALLOWED, '');
  const bare = letters.replace(APOSTROPHES, '');
  if (bare === bare.toUpperCase() || bare === bare.toLowerCase()) {
    return letters
      .toLowerCase()
      .replace(/(^|[-'’])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
  }
  return letters;
}

/**
 * `[title] [courtesy] [name] [suffix]`, with "St" before a name said "Saint".
 * A title the name already opens with is not said twice ("Nurse Practitioner
 * Jane Smith, NP").
 */
function joinSpoken(
  title: string | null,
  courtesy: readonly string[],
  words: readonly string[],
  suffix: string | null
): string {
  const said = words.map((w, i) => (keyOf(w) === 'st' && i < words.length - 1 ? 'Saint' : w));
  if (said.length === 0 && courtesy.length === 0) return title ?? '';
  const opening = [...courtesy, ...said].join(' ').toLowerCase();
  const t = title?.toLowerCase();
  const titled = t && opening !== t && !opening.startsWith(`${t} `) ? title : null;
  return [titled, ...courtesy, ...said, suffix].filter(Boolean).join(' ');
}

// -----------------------------------------------------------------------------
// Reading a written name
// -----------------------------------------------------------------------------

export interface ParsedName {
  /** The name's words in speaking order, as written (periods dropped). */
  words: string[];
  /** Credentials found, in their canonical spelling ("MD", "PhD"), in order. */
  credentials: string[];
  /** A title written before the name, as the word said ("Doctor"); null when none. */
  writtenTitle: string | null;
  /** Courtesy titles as written ("Ms"), kept only so a typed line can be checked. */
  courtesy: string[];
  /** A generational suffix as the words said ("Junior", "the Third"); null when none. */
  suffix: string | null;
  /** The suffix as written ("Jr"), for a display name; null when none. */
  writtenSuffix: string | null;
}

/** A token's lookup key: lower-cased, periods dropped ("M.D." → "md"). */
function keyOf(token: string): string {
  return token.replace(/\./g, '').toLowerCase();
}

/**
 * Split a written name into its parts. Shared by the spoken line, the
 * referring doctor's line, and the API's display name, so every surface agrees
 * on what in a chart name is a credential.
 *
 * A KNOWN CREDENTIAL IS ONLY A CREDENTIAL WHERE IT IS WRITTEN AS ONE. It
 * counts after a comma ("Sahai, MD"), when dotted ("M.D."), when written in its
 * own capitals in a name that is not all capitals ("Sahai MD", "Smith PhD"), or
 * as the last word of an all-capitals or all-lowercase name, after at least one
 * name word ("SAHAI MD"). So "John Do" keeps its surname, while "JOHN DO" reads
 * as John with a DO: there is no list of surnames to protect, deliberately — a
 * practice whose provider is said wrong corrects the spoken name.
 *
 * `lastNameFirst` reads "Smith, John" as John Smith: exactly two comma parts
 * that hold a name, unless the second opens with a credential — the EHR's
 * "Anurag, MD Sahai, MD" is already first-name-first.
 */
export function parseName(raw: string, opts: { lastNameFirst?: boolean } = {}): ParsedName {
  // An annotation in brackets ("Smith (Locum)") is about the name, not part of it.
  const text = normalizeSpokenName(raw.replace(/\([^)]*\)|\[[^\]]*\]/g, ' '));
  // All capitals or all lowercase: case says nothing about what is a credential.
  const caseless =
    /\p{L}/u.test(text) && (text === text.toUpperCase() || text === text.toLowerCase());
  // Empty parts stay, so "after a comma" means after one in the text.
  const segments = text.split(',').map((s) => s.trim().split(' ').filter(Boolean));
  const lastSegment = segments.reduce((last, tokens, i) => (tokens.length > 0 ? i : last), -1);

  const found: Omit<ParsedName, 'words'> = {
    credentials: [],
    writtenTitle: null,
    courtesy: [],
    suffix: null,
    writtenSuffix: null,
  };
  const nameSegments: { words: string[]; opensWithCredential: boolean }[] = [];

  segments.forEach((tokens, s) => {
    const words: string[] = [];
    let opensWithCredential = false;
    tokens.forEach((token, t) => {
      const key = keyOf(token);
      const bare = token.replace(/\./g, '');
      const hasMore = t < tokens.length - 1;
      const nameSoFar = words.length + priorWords(nameSegments);
      const credential = CREDENTIAL_BY_KEY.get(key);
      if (credential) {
        const isLastToken = s === lastSegment && !hasMore;
        const writtenAsOne =
          s > 0 ||
          (token.includes('.') && bare.length > 1) ||
          (!caseless && bare === credential.written) ||
          (caseless && isLastToken && nameSoFar >= 1);
        if (writtenAsOne) {
          found.credentials.push(credential.written);
          if (t === 0) opensWithCredential = true;
          return;
        }
      }
      // An unlisted credential after a comma, once a whole name ("Jane Doe,
      // CCC-SLP") or a listed credential ("SMITH, MD, MPH") came before it. A
      // lone surname before it means "Last, FIRST": "Devineni, ADITYA".
      if (
        !credential &&
        s > 0 &&
        UNLISTED_CREDENTIAL.test(bare) &&
        (nameSoFar >= 2 || found.credentials.length > 0)
      ) {
        found.credentials.push(bare);
        if (t === 0) opensWithCredential = true;
        return;
      }
      // A title opens the name: the first word of its comma part, with more after it.
      if (words.length === 0 && hasMore && TITLES.has(key)) {
        found.writtenTitle ??= TITLES.get(key) ?? null;
        return;
      }
      if (words.length === 0 && hasMore && COURTESY_TITLES.has(key)) {
        found.courtesy.push(bare);
        return;
      }
      // A suffix closes it: after at least one name word, at the end of its part.
      if (nameSoFar > 0 && !hasMore && SUFFIXES.has(key)) {
        found.suffix = SUFFIXES.get(key) ?? null;
        found.writtenSuffix = bare;
        return;
      }
      words.push(bare);
    });
    if (words.length > 0) nameSegments.push({ words, opensWithCredential });
  });

  let ordered = nameSegments;
  const [last, first] = nameSegments;
  if (
    opts.lastNameFirst &&
    nameSegments.length === 2 &&
    first &&
    last &&
    !first.opensWithCredential
  ) {
    ordered = [first, last];
  }

  return { words: dedupeAdjacent(ordered.flatMap((seg) => seg.words)), ...found };
}

function priorWords(segments: readonly { words: string[] }[]): number {
  return segments.reduce((n, seg) => n + seg.words.length, 0);
}

/** "Joanne Joanne Lister" → "Joanne Lister" — the EHR repeats a first name into the last. */
function dedupeAdjacent(words: readonly string[]): string[] {
  const out: string[] = [];
  for (const word of words) {
    if (out[out.length - 1]?.toLowerCase() !== word.toLowerCase()) out.push(word);
  }
  return out;
}
